import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { CrudPage } from "@/components/crud/CrudPage";
import { DataTable } from "@/components/DataTable";
import { getModules, type Module } from "@/api/module.service";
import { getModulesByClient } from "@/api/client.service";
import { getUserClients, UserRoles } from "@/api/user.service";
import { useAuth } from "@/context/AuthContext";
import { useNotification } from "@/context/NotificationContext";
import usePagination from "@/hooks/pagination";
import { IconButton } from "@mui/material";
import { Sync, Visibility } from "@mui/icons-material";

export function ModulesPage() {
  const { user } = useAuth();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const isAdmin = user?.role === UserRoles.Admin;

  const { page, setPage, rowsPerPage, setRowsPerPage } = usePagination(0, 10);

  const [modules, setModules] = useState<Module[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const loadModules = async () => {
      setIsLoading(true);
      try {
        const allModules = await getModules();

        if (isAdmin) {
          setModules(allModules);
          return;
        }

        let clientId = user.loggedClient?.id;
        if (!clientId) {
          const clients = await getUserClients(user.id);
          clientId = clients.length === 1 ? clients[0].id : undefined;
        }

        if (!clientId) {
          setModules([]);
          return;
        }

        const clientModules = await getModulesByClient(clientId);
        const allowedIds = new Set(clientModules.map((m) => m.module_id));
        setModules(allModules.filter((m) => allowedIds.has(m.id)));
      } catch (error) {
        console.error("Erro ao carregar módulos:", error);
        notify("Erro ao carregar módulos", "error");
      } finally {
        setIsLoading(false);
      }
    };

    loadModules();
  }, [user, isAdmin]);

  const paginatedModules = useMemo(
    () => modules.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [modules, page, rowsPerPage]
  );

  const handleOpenModule = (module: Module) => {
    navigate(`/modulos/${module.id}/usuarios`);
  };

  if (isLoading) return <Sync />;

  return (
    <CrudPage
      title="Módulos"
      subtitle="Veja quais usuários têm acesso a cada módulo, e por qual empresa"
    >
      <DataTable
        data={paginatedModules}
        columns={[
          { key: "label", label: "Módulo" },
          { key: "description", label: "Descrição" },
        ]}
        total={modules.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        onRowClick={handleOpenModule}
        actions={(module) => (
          <IconButton onClick={() => handleOpenModule(module)}>
            <Visibility />
          </IconButton>
        )}
      />
    </CrudPage>
  );
}
